/**
 * AI Tool for setting up Stripe integration in projects
 * Helps AI assistants scaffold Stripe payment functionality
 */

import type { Tool } from './Tool';
import type { JSRuntimeFS } from '@/lib/JSRuntime';
import { setupStripeInProject } from '@/lib/integrations/stripe-project-setup';
import {
  createStripeProduct,
  createStripePrice,
  getStripeAccessToken,
} from '@/lib/integrations/stripe';
import type { StripeIntegration } from '@/contexts/IntegrationsContext';

export interface StripeSetupParams {
  /** Project directory path */
  projectPath: string;
  /** Platform to deploy to (netlify or vercel) */
  platform: 'netlify' | 'vercel';
  /** Which serverless functions to include */
  functions?: Array<'payment-intent' | 'checkout-session' | 'subscription' | 'webhook'>;
  /** Products to create in Stripe (optional) */
  products?: Array<{
    name: string;
    description?: string;
    price: number;
    currency?: string;
    recurring?: { interval: 'day' | 'week' | 'month' | 'year' };
  }>;
}

export class StripeSetupTool implements Tool<StripeSetupParams> {
  description =
    'Sets up Stripe payment integration in a project. Creates serverless functions for secure payment processing, adds necessary dependencies, and optionally creates products in the connected Stripe account.';

  constructor(
    private fs: JSRuntimeFS,
    private getStripeIntegration: () => StripeIntegration | null
  ) {}

  async execute(params: StripeSetupParams): Promise<string> {
    const { projectPath, platform, functions, products } = params;

    // Get Stripe integration
    const integration = this.getStripeIntegration();
    if (!integration) {
      return 'Error: No Stripe integration found. User must connect their Stripe account in Settings > Integrations first.';
    }

    try {
      // Set up Stripe in the project
      await setupStripeInProject(this.fs, {
        projectDir: projectPath,
        integration,
        platform,
        functions,
      });

      let result = `✓ Stripe integration set up successfully!\n\n`;
      result += `Created serverless functions:\n`;
      const functionsList = functions || ['payment-intent', 'checkout-session'];
      functionsList.forEach((f) => {
        result += `  - ${f}\n`;
      });

      result += `\nPublishable key: ${integration.publishableKey}\n`;
      result += `Mode: ${integration.livemode ? 'Live' : 'Test'}\n`;

      // Create products if requested
      if (products && products.length > 0) {
        result += `\n📦 Creating products in Stripe...\n`;

        const accessToken = await getStripeAccessToken(integration);
        const createdProducts: any[] = [];

        for (const productData of products) {
          try {
            // Create product
            const product = await createStripeProduct(
              accessToken,
              productData.name,
              productData.description
            );

            // Create price
            const price = await createStripePrice(
              accessToken,
              product.id,
              productData.price,
              productData.currency || 'usd',
              productData.recurring
            );

            createdProducts.push({ product, price });

            const priceDisplay = (productData.price / 100).toFixed(2);
            const currency = (productData.currency || 'usd').toUpperCase();
            const recurring = productData.recurring
              ? ` (${productData.recurring.interval}ly)`
              : '';

            result += `  ✓ ${productData.name}: $${priceDisplay} ${currency}${recurring}\n`;
            result += `    Product ID: ${product.id}\n`;
            result += `    Price ID: ${price.id}\n`;
          } catch (error) {
            result += `  ✗ Failed to create ${productData.name}: ${error instanceof Error ? error.message : 'Unknown error'}\n`;
          }
        }

        // Generate code snippet for using the products
        if (createdProducts.length > 0) {
          result += `\n💡 Use these price IDs in your code:\n\n`;
          result += `const priceIds = {\n`;
          createdProducts.forEach(({ product, price }, index) => {
            const productData = products[index];
            const key = productData.name
              .toLowerCase()
              .replace(/[^a-z0-9]+/g, '_')
              .replace(/^_|_$/g, '');
            result += `  ${key}: '${price.id}',  // ${productData.name}\n`;
          });
          result += `};\n`;
        }
      }

      result += `\n📝 Next steps:\n`;
      result += `1. Check ${projectPath}/STRIPE_SETUP.md for detailed instructions\n`;
      result += `2. When deploying to ${platform}, add these environment variables:\n`;
      result += `   - VITE_STRIPE_PUBLISHABLE_KEY=${integration.publishableKey}\n`;
      result += `   - STRIPE_SECRET_KEY=<your_secret_key>\n`;
      result += `3. Test your payment integration before going live\n`;

      return result;
    } catch (error) {
      return `Error setting up Stripe: ${error instanceof Error ? error.message : 'Unknown error'}`;
    }
  }
}
